import React, { useState } from 'react';
import { Stethoscope, Activity, Pill, Monitor, Clock, DollarSign, ChevronDown, ChevronUp, GraduationCap, Award } from 'lucide-react';
import { shortCourses, applicationFormUrl } from '../data/mock';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';

const iconMap = {
  Stethoscope: Stethoscope,
  Activity: Activity,
  Pill: Pill,
  Monitor: Monitor
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-TZ').format(amount);
};

const CourseCategory = ({ category, data, isExpanded, onToggle }) => {
  const IconComponent = iconMap[data.icon] || GraduationCap;
  
  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow duration-300">
      {/* Category Header */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 flex items-center justify-between bg-gradient-to-r from-[#1e3a5f] to-[#2a4a6f] text-white hover:from-[#2a4a6f] hover:to-[#1e3a5f] transition-all duration-300"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
            <IconComponent className="w-6 h-6" />
          </div>
          <div className="text-left">
            <h3 className="text-xl font-bold">{data.category} Short Courses</h3>
            <p className="text-white/80 text-sm">{data.courses.length} courses available</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline-block px-3 py-1 bg-[#d4a855] text-[#1e3a5f] rounded-full text-sm font-semibold">
            {data.courses.length} Courses
          </span>
          {isExpanded ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
        </div>
      </button>

      {/* Category Description */}
      {isExpanded && (
        <div className="px-6 py-4 bg-gray-50 border-b">
          <p className="text-gray-600">{data.description}</p>
        </div>
      )}

      {/* Courses Table */}
      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#f8fafc]">
                <th className="px-6 py-4 text-left text-xs font-bold text-[#1e3a5f] uppercase tracking-wider">S/N</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-[#1e3a5f] uppercase tracking-wider">Course Title</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-[#1e3a5f] uppercase tracking-wider">Duration</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-[#1e3a5f] uppercase tracking-wider">Fee (TZS)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.courses.map((course, index) => (
                <tr 
                  key={course.id} 
                  className="hover:bg-[#fef9f0] transition-colors duration-150"
                >
                  <td className="px-6 py-4">
                    <span className="w-8 h-8 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-gray-900 font-medium">{course.title}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4 text-[#d4a855]" />
                      <span>{course.duration}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <span className="text-[#1e3a5f] font-bold">{formatCurrency(course.fee)}</span>
                      <span className="text-gray-500 text-sm">/=</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ShortCourses = () => {
  const [expandedCategories, setExpandedCategories] = useState({
    clinicalMedicine: true,
    physiotherapy: false,
    pharmaceuticalSciences: false,
    informationTechnology: false
  });

  const toggleCategory = (key) => {
    setExpandedCategories(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const expandAll = () => {
    setExpandedCategories({
      clinicalMedicine: true,
      physiotherapy: true,
      pharmaceuticalSciences: true,
      informationTechnology: true
    });
  };

  const collapseAll = () => {
    setExpandedCategories({
      clinicalMedicine: false,
      physiotherapy: false,
      pharmaceuticalSciences: false,
      informationTechnology: false
    });
  };

  const totalCourses = Object.values(shortCourses).reduce(
    (sum, cat) => sum + cat.courses.length, 0
  );

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1e3a5f] via-[#2a4a6f] to-[#1e3a5f] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>
        <div className="container-custom relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#d4a855]/20 text-[#d4a855] px-4 py-2 rounded-full mb-6">
              <Award className="w-5 h-5" />
              <span className="font-medium">Professional Development</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading mb-6">
              Professional Short Courses
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Advance your career with industry-recognized certifications and specialized training programs designed for working professionals.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4">
                <p className="text-3xl font-bold text-[#d4a855]">{totalCourses}</p>
                <p className="text-white/70 text-sm">Courses Available</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4">
                <p className="text-3xl font-bold text-[#d4a855]">4</p>
                <p className="text-white/70 text-sm">Specializations</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4">
                <p className="text-3xl font-bold text-[#d4a855]">Flexible</p>
                <p className="text-white/70 text-sm">Duration Options</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="py-16">
        <div className="container-custom">
          {/* Controls */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <h2 className="text-2xl font-bold text-[#1e3a5f]">Browse by Category</h2>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={expandAll}
                className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
              >
                Expand All
              </Button>
              <Button 
                variant="outline" 
                onClick={collapseAll}
                className="border-gray-300 text-gray-600 hover:bg-gray-100"
              >
                Collapse All
              </Button>
            </div>
          </div>

          {/* Course Categories */}
          <div className="space-y-6">
            {Object.entries(shortCourses).map(([key, data]) => (
              <CourseCategory
                key={key}
                category={key}
                data={data}
                isExpanded={expandedCategories[key]}
                onToggle={() => toggleCategory(key)}
              />
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-r from-[#d4a855] to-[#e5b966] rounded-2xl p-8 md:p-12 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-4">
              Ready to Advance Your Career?
            </h3>
            <p className="text-[#1e3a5f]/80 mb-6 max-w-2xl mx-auto">
              Enroll in our professional short courses and gain the skills employers are looking for. 
              Flexible schedules available for working professionals.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#1e3a5f] hover:bg-[#2a4a6f] text-white px-8 py-3 text-lg rounded-full">
                  Apply Now
                </Button>
              </a>
              <a href="mailto:info@bit.ac.tz">
                <Button variant="outline" className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white px-8 py-3 text-lg rounded-full">
                  Contact Us
                </Button>
              </a>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-[#1e3a5f]/10 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-[#1e3a5f]" />
              </div>
              <h4 className="text-lg font-bold text-[#1e3a5f] mb-2">Flexible Duration</h4>
              <p className="text-gray-600 text-sm">Courses range from 2 weeks to 6 months, designed to fit your schedule.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-[#1e3a5f]/10 flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-[#1e3a5f]" />
              </div>
              <h4 className="text-lg font-bold text-[#1e3a5f] mb-2">Certified Programs</h4>
              <p className="text-gray-600 text-sm">Receive recognized certificates upon successful completion of each course.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-[#1e3a5f]/10 flex items-center justify-center mb-4">
                <GraduationCap className="w-6 h-6 text-[#1e3a5f]" />
              </div>
              <h4 className="text-lg font-bold text-[#1e3a5f] mb-2">Expert Instructors</h4>
              <p className="text-gray-600 text-sm">Learn from industry professionals with years of practical experience.</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ShortCourses;
